
import { Vue, Options } from "vue-class-component";
import CargoV2EditHeaderActions from "./edit/cargo-v2-edit-header-actions.vue";
import CargoV2EditHeaderTitle from "./edit/cargo-v2-edit-header-title.vue";
import { MainAppController } from "../../controllers/MainAppController";
import { CargoV2Controller } from "../../controllers/CargoV2Controller";
import {
  CargoV2Data,
  FormCargoV2,
  ActualDimensionData,
  BagOrSttDetail,
  CreateCargoV3,
  CalculateVolume
} from "@/domain/entities/CargoV2";
import parsingErrorResponse from "@/app/infrastructures/misc/common-library/ParsingErrorResponse";
import { isPlaneNogValid } from "./utils";
import CargoV2ManualInput from "./components/manual-input/cargo-v2-manual-input.vue";
import ScanTableSection from "./components/scan-table-section.vue";
import { OptionsClass, Snackbar } from "@/domain/entities/MainApp";
import {
  CalculateVolumeApiRequestV2,
  CalculateVolumeRequestV2,
  CreateCargoV3ApiRequest
} from "@/data/payload/api/CargoV2ApiRequest";
import {
  CALCULATE_TYPE,
  getConfigDimensionDivider,
  getConfigIsminimumVolumePerPiece,
  getConfigMinimumVolumePerPiece
} from "./modules/cargo-module";
import CargoDisputeForm from "./components/cargo-dispute-form/index.vue";
import { CheckEligible, useCargoV2 } from "./composables";
import { AccountController } from "../../controllers/AccountController";
import { cloneDeep, startCase } from "lodash";
import { ConvertObjectCamelToSnakeCaseV3 } from "@/app/infrastructures/misc/common-library/ConvertObjectCamelToSnakeCase";

const { onCheckEligible } = useCargoV2();

@Options({
  components: {
    CargoV2EditHeaderTitle,
    CargoV2EditHeaderActions,
    CargoV2ManualInput,
    ScanTableSection,
    CargoDisputeForm
  },
  beforeRouteLeave(to: any, from: any, next: any) {
    if (!this.toSelectFLight) this.onReset();
    next();
  },
  watch: {
    isUploadProofPhoto: {
      handler(bool: boolean) {
        if (!bool && !this.isFirstLanding)
          CargoV2Controller.setForm({ photoProof: [] });
      }
    }
  }
})
class CargoV2Rebooking extends Vue {
  toSelectFLight = false;
  isFirstLanding = true;
  get id() {
    return String(this.$route.params.id);
  }

  async mounted() {
    if (!this.form.rebookingId) {
      await this.fetchDetailRetryCargo();
    } else {
      this.setDefaultProofPhoto();
    }
    CargoV2Controller.setInitCalculateVolumeData(
      new CalculateVolume({
        chargeableWeight: this.form.currentChargeableWeight,
        calculateType: this.form.calculateType || CALCULATE_TYPE.INIT
      })
    );
    await this.$nextTick();
    await this.onCalculateVolume(this.form.calculateType);
    await this.$nextTick();
    this.$router.replace({
      query: {
        params: `${this.form.originCityCode}-${this.form.destinationCityCode
          .code || this.form.destinationCityCode.value}`
      }
    });
    const el = document.querySelector(
      `[data-content="${this.$route.params.id}"]`
    );
    if (el) el.remove(); // remove breadcrumb
    this.isFirstLanding = false;
  }

  get form() {
    return CargoV2Controller.form;
  }

  async fetchDetailRetryCargo() {
    try {
      MainAppController.showLoading();
      const res = await CargoV2Controller.getDetailRetryCargo(this.id);
      CargoV2Controller.setForm(this.mapDetailToForm(res));
      CargoV2Controller.setProofPhotoRebooking(res.photoProof);
      CargoV2Controller.onGenerateGroupDataTable(this.mapRcDetail(res));
      CargoV2Controller.onGenerateDominanCommodityGroup();
      if (!res.nog && !this.form.nog) {
        CargoV2Controller.onGenerateDefaultNog();
      }
      await onCheckEligible(
        new CheckEligible({
          bagOrStt: this.mapRcDetail(res),
          ineligible: () => {
            if (this.form.bagOrStt.length) {
              this.form.actualTotalCargoVolumeweight = this.form.totalAccumulationEstimationVolumeWeightDataTable;
              this.form.cargoActualPiece = this.form.totalAccumulationActualPieceDataTable;
              this.form.actualTotalCargoGrossweigth = this.form.totalAccumulationEstimationGrossWeightDataTable;
            }
          },
          close: () => {
            if (!this.form.bagOrStt.length) {
              this.goBack();
            }
          }
        })
      );
    } catch (err) {
      MainAppController.showErrorMessage(
        parsingErrorResponse(
          err,
          this.$t("cargoV2.components.common.failedGetData"),
          () => {
            this.fetchDetailRetryCargo();
            MainAppController.closeErrorMessage();
          },
          () => {
            this.goBack();
          }
        )
      );
    } finally {
      MainAppController.closeLoading();
    }
  }

  mapDetailToForm(res: CargoV2Data) {
    return new FormCargoV2({
      ...res,
      rebookingId: res.id,
      originAirportCode: res.airportOriginCityCode,
      destinationAirportCode: res.airportDestinationCityCode,
      estTotalCargoGrossweigth: res.totalEstCargoGrossWeight,
      estTotalCargoVolumeweight: res.totalEstCargoVolumeWeight,
      actualTotalCargoGrossweigth: res.totalActualCargoGrossWeight,
      actualTotalCargoVolumeweight: res.totalActualCargoVolumeWeight,
      totalPiece: res.totalPieces,
      cargoCommodity: new OptionsClass({
        name: res.cargoCommodity,
        code: res.cargoCommodityCode
      }),
      productCargo: res.cargoProduct,
      shc: res.shcCode,
      shcName: res.shcDescription,
      cargoType: new OptionsClass({ value: res.cargoType }),
      actualDimension: new ActualDimensionData({
        cargoActualLengthDimension: res.actualDimension.length,
        cargoActualHeightDimension: res.actualDimension.height,
        cargoActualWidthDimension: res.actualDimension.width
      }),
      destinationCityCode: new OptionsClass({
        label: `${res.destinationCityCode} - ${startCase(
          res.destinationCityName?.toLowerCase()
        )}`,
        code: res.destinationCityCode,
        name: startCase(res.destinationCityName?.toLowerCase()),
        countryCode: res.destinationCountryCode
      }),
      nog: res.nog
    });
  }

  mapRcDetail(res: CargoV2Data) {
    return res.detailStt.length
      ? res.detailStt.map(
          (item) =>
            new BagOrSttDetail({
              bagNo: item.baggingNumber,
              bagVendorNo: item.baggingVendorNumber,
              sttNo: item.sttNumber,
              sttNoElexys: item.sttElexysNumber || "",
              sttTotalPiece: item.sttTotalPiece || item.totalPieces || 0,
              grossWeight: item.grossWeight || item.sttGrossWeight || 0,
              volumeWeight: item.volumeWeight || item.sttVolumeWeight || 0,
              chargeableWeight:
                // item.bagTotalChargeableWeight ||
                // item.bagCustomChargeableWeight ||
                // item.chargeableWeight ||
                item.totalSttChargeableWeight || 0,
              productType: item.productType || item.sttProductType || "",
              commodityId: 0,
              commodityName: item.sttCommodityName,
              commodityCode: item.sttCommodityName,
              commodityGroupCode: item.commodityGroupCode || "",
              commodityGroupName: item.commodityGroupName || "",
              commodityGroupIsDg: !!item.commodityGroupIsDg,
              destinationCityCode: item.destinationCityCode || "",
              destinationCityName: item.destinationCityName || "",
              destinationDistrictCode: "",
              destinationDistrictName: "",
              shcCode: item.shcCode || "",
              shcDescription: item.shcName || "",
              sttPieces: item.sttPieces || [],
              isDangerousGoods: item.isDangerousGoods,
              isQuarantine: item.isQuarantine,
              bagData: item.bagData,
              sttClientId: item.sttClientId
            })
        )
      : [];
  }

  get loadingCalculate() {
    return CargoV2Controller.isLoadingCalculateVolume;
  }

  async onCalculateVolume(calculateType: string) {
    return await CargoV2Controller.calculateVolume(
      new CalculateVolumeApiRequestV2({
        calculateType: calculateType,
        calculateRequest: new CalculateVolumeRequestV2({
          cargoType: this.form.cargoType.value,
          chargeableWeightReweigh: this.form.cargoTotalEstimateChargeableWeight,
          currentGrossWeight: this.form.actualTotalCargoGrossweigth,
          height: this.form.actualDimension.cargoActualHeightDimension,
          length: this.form.actualDimension.cargoActualLengthDimension,
          width: this.form.actualDimension.cargoActualWidthDimension,
          totalPieces: this.form.cargoActualPiece,
          volumeWeightReweigh: this.form
            .totalAccumulationEstimationVolumeWeightDataTable
        })
      })
    );
  }

  goBack() {
    this.onReset();
    const rcNo = this.$route.query?.rcNo;
    const cargoNo = this.$route.query?.cargoNo;
    if (rcNo && cargoNo) {
      this.$router.replace({
        path: `/cargo-v2/${cargoNo}/info`,
        query: ConvertObjectCamelToSnakeCaseV3({ rcNo })
      });
    } else {
      this.$router.replace("/cargo-v2/booking-cancelled");
    }
  }

  async handleSelectModa(moda: string) {
    await onCheckEligible(
      new CheckEligible({
        bagOrStt: this.form.bagOrStt,
        ineligible: () => {
          if (this.form.bagOrStt.length) {
            this.form.actualTotalCargoVolumeweight = this.form.totalAccumulationEstimationVolumeWeightDataTable;
            this.form.cargoActualPiece = this.form.totalAccumulationActualPieceDataTable;
            this.form.actualTotalCargoGrossweigth = this.form.totalAccumulationEstimationGrossWeightDataTable;
          }
        },
        close: () => {
          if (!this.form.bagOrStt.length) {
            this.goBack();
          }
        }
      })
    );
    CargoV2Controller.setForm({
      cargoType: new OptionsClass({ value: moda }),
      cargoNo: "",
      driverSelected: "",
      cargoVehicleNo: "",
      isValidVehicleNo: false,
      isValidAwbManual: false
    });
    await this.onCalculateVolume(CALCULATE_TYPE.CHANGE_MODA);
  }

  handleSelectSchedule() {
    onCheckEligible(
      new CheckEligible({
        success: this.redirectSelectSchedule,
        ineligible: () => {
          if (this.form.bagOrStt.length) {
            this.form.actualTotalCargoVolumeweight = this.form.totalAccumulationEstimationVolumeWeightDataTable;
            this.form.cargoActualPiece = this.form.totalAccumulationActualPieceDataTable;
            this.form.actualTotalCargoGrossweigth = this.form.totalAccumulationEstimationGrossWeightDataTable;
          }
        },
        close: () => {
          if (!this.form.bagOrStt.length) {
            this.goBack();
          } else {
            this.onCalculateVolume(CALCULATE_TYPE.INIT);
          }
        }
      })
    );
  }

  redirectSelectSchedule() {
    this.toSelectFLight = true;
    this.$router.push(`/cargo-v2/${this.id}/rebooking/search-flight`);
  }

  handleBooking() {
    onCheckEligible(new CheckEligible({ success: this.onBooking }));
  }

  async onBooking() {
    try {
      MainAppController.showLoading();
      MainAppController.closeMessageModal();
      MainAppController.closeErrorMessage();

      await this.onCalculateVolume(CargoV2Controller.calculateType);

      CargoV2Controller.setDefaultDepartureArrivalNonPlane();

      const res = await CargoV2Controller.createBooking(
        new CreateCargoV3ApiRequest({
          ...this.form,
          totalStt: CargoV2Controller.totalSttDataTable,
          totalPiece: this.form.totalAccumulationActualPieceDataTable,
          isUseNewCalculation: true,
          calculateType: CargoV2Controller.calculateType
        })
      );

      this.onSuccessCreate(res);
    } catch (error) {
      MainAppController.showErrorMessage(
        parsingErrorResponse(
          error,
          AccountController.accountData.accountIsForeign
            ? "Failed to Cargo Booking !"
            : "Pembuatan Gagal !",

          this.onBooking
        )
      );
    } finally {
      MainAppController.closeLoading();
    }
  }

  onSuccessCreate(resp: CreateCargoV3) {
    const { destinationCityCode } = cloneDeep(this.form);
    this.$router.replace("/cargo-v2/booking-success");
    window.requestAnimationFrame(() => {
      MainAppController.setSnackbarData(
        new Snackbar({
          type: "success",
          message: this.$t("cargoV2.create.snackbar.booked", {
            cargoNo: resp.cargoNo,
            destination: destinationCityCode.code
          })
        })
      );
      MainAppController.setAutoClose(true);
      MainAppController.setSnackbar(true);
    });
    CargoV2Controller.setForm(new FormCargoV2());
  }

  get isValidSearchFlight() {
    const { cargoType, cargoNo } = this.form;

    return [cargoType.value === "plane", !cargoNo, this.isValidForm].every(
      Boolean
    );
  }

  get isUploadProofPhoto() {
    return CargoV2Controller.isUploadProofPhoto;
  }

  get isValidDisputeForm() {
    return [
      !CargoV2Controller.isPiecesInputError,
      !CargoV2Controller.isGrossWeightInputError,
      !CargoV2Controller.isVolumeWeightError,
      this.isUploadProofPhoto
        ? this.form.photoProof
            .filter(Boolean)
            .filter((p) => typeof p === "string").length
        : true,
      !CargoV2Controller.isExceedMaxDisputeWeight,
      !CargoV2Controller.isBagOnlyExceedMaxDisputeWeight
    ].every(Boolean);
  }

  get isValidBooking() {
    const { isValidAwbManual, cargoNo, cargoType } = this.form;

    const isShipValid = cargoType.value !== "ship" || cargoNo;
    const isPlaneValid = cargoType.value !== "plane" || isValidAwbManual;

    return [isShipValid, isPlaneValid, this.isValidForm].every(Boolean);
  }

  get isValidForm() {
    const {
      destinationCityCode,
      cargoType,
      isValidVehicleNo,
      cargoCommodity,
      productCargo,
      bagOrStt,
      cargoActualPiece,
      actualTotalCargoGrossweigth,
      actualTotalCargoVolumeweight,
      actualDimension: {
        cargoActualHeightDimension,
        cargoActualLengthDimension,
        cargoActualWidthDimension
      }
    } = this.form;

    const isVehicleValid = isValidVehicleNo || cargoType.value === "plane";

    const isNogValid = isPlaneNogValid(cargoType.value, this.form.nog);

    return [
      destinationCityCode.code,
      cargoType.value,
      isVehicleValid,
      cargoCommodity.code,
      productCargo,
      cargoActualPiece,
      actualTotalCargoGrossweigth,
      actualTotalCargoVolumeweight,
      cargoActualHeightDimension,
      cargoActualLengthDimension,
      cargoActualWidthDimension,
      bagOrStt.length,
      this.isValidDisputeForm,
      isNogValid
    ].every(Boolean);
  }

  get isValidSelectTransport() {
    return [
      !CargoV2Controller.isExceedMaxDisputeWeight,
      !CargoV2Controller.isBagOnlyExceedMaxDisputeWeight
    ].every(Boolean);
  }

  onReset() {
    CargoV2Controller.setForm(new FormCargoV2());
  }

  setDefaultProofPhoto() {
    if (
      JSON.stringify(this.form.photoProof) !==
      JSON.stringify(CargoV2Controller.proofPhotoRebooking)
    ) {
      CargoV2Controller.setForm({ photoProof: [] });
    }
  }
}
export default CargoV2Rebooking;
